import { ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import { useTranslations } from 'use-intl';
import { useMediaQuery } from 'usehooks-ts';

import { ActivityReportUnitsTable } from '@plot/plot-api';
import { Table2, Tooltip } from '@plot/ui';

import PriceDisplay from '@/components/common/PriceDisplay';

const useColumns = () => {
  const isMd = useMediaQuery('(min-width: 768px)'); // Tailwind's md breakpoint
  const is2xl = useMediaQuery('(min-width: 1536px)'); // Tailwind's 2xl breakpoint
  const t = useTranslations('ActivityReports.ActivityReport.PriceChanges');

  const columnHelper = createColumnHelper<ActivityReportUnitsTable>();

  const columns = [
    columnHelper.accessor('name', {
      header: () => (
        <div className="flex items-center gap-1">{t('table.name')}</div>
      ),
      cell: ({ row }) => (
        <span className="text-left line-clamp-1">
          <Tooltip content={row.original.name || '-'}>
            <span className="break-words line-clamp-1">
              {row.original.name || '-'}
            </span>
          </Tooltip>
        </span>
      ),
      enableSorting: true,
      size: is2xl ? 148 : isMd ? 148 : 110,
    }),
    columnHelper.accessor('project.name', {
      header: () => t('table.project'),
      cell: ({ row }) => (
        <span className="text-left line-clamp-1">
          <Tooltip content={row.original.project.name || '-'}>
            <span className="break-words line-clamp-1">
              {row.original.project.name || '-'}
            </span>
          </Tooltip>
        </span>
      ),
      enableSorting: true,
      size: is2xl ? 180 : 180,
    }),
    columnHelper.accessor('forSaleAt', {
      header: () => t('table.forSaleAt'),
      cell: ({ row }) => <span>{row.original.forSaleAt || '-'}</span>,
      size: is2xl ? 100 : 100,
      enableSorting: true,
    }),
    columnHelper.accessor('floor', {
      header: () => t('table.floor'),
      cell: ({ row }) => <span>{row.original.floor || '-'}</span>,
      size: is2xl ? 70 : 70,
      enableSorting: true,
    }),
    columnHelper.accessor('rooms', {
      header: () => t('table.rooms'),
      cell: ({ row }) => <span>{row.original.rooms || '-'}</span>,
      size: is2xl ? 80 : 80,
      enableSorting: true,
    }),
    columnHelper.accessor('usableArea', {
      header: () => t('table.usableArea'),
      cell: ({ row }) => (
        <>
          <span>{row.original.usableArea || '-'}</span>{' '}
          <span className="pl-0.5 lowercase text-neutral-80">m²</span>
        </>
      ),
      size: is2xl ? 60 : 60,
      enableSorting: true,
    }),
    columnHelper.accessor('sellingPrice', {
      header: () => t('table.sellingPrice'),
      cell: ({ row }) => (
        <>
          <PriceDisplay value={row.original.sellingPrice} />
        </>
      ),
      size: is2xl ? 100 : 100,
      enableSorting: true,
    }),
    columnHelper.accessor('totalSellingPriceChange', {
      header: () => t('table.totalSellingPriceChange'),
      cell: ({ row }) => (
        <>
          <PriceDisplay value={row.original.totalSellingPriceChange} compact />
        </>
      ),

      size: is2xl ? 100 : 100,
      enableSorting: true,
    }),
    columnHelper.accessor('percentageSellingPriceChange', {
      header: () => t('table.percentageSellingPriceChange'),
      cell: ({ row }) => {
        const percentage = row.original.percentageSellingPriceChange;
        const isPositive = (percentage ?? 0) > 0;

        return (
          <>
            <div
              className={clsx(
                'flex items-center gap-1',
                isPositive
                  ? 'text-success'
                  : percentage !== 0
                  ? 'text-error'
                  : 'text-neutral-40'
              )}
            >
              {' '}
              {isPositive ? (
                <ArrowUp size={14} />
              ) : (
                percentage !== 0 && <ArrowDown size={14} />
              )}
              {Math.abs(percentage ?? 0).toFixed(2)} %
            </div>
          </>
        );
      },

      size: is2xl ? 128 : 124,
      enableSorting: true,
    }),
    columnHelper.accessor('finalizedAt', {
      header: () => t('table.finalizedAt'),
      cell: ({ row }) => <span>{row.original.finalizedAt || '-'}</span>,
      size: is2xl ? 100 : 100,
      enableSorting: true,
    }),
    columnHelper.accessor('daysOnMarket', {
      header: () => t('table.daysOnMarket'),
      cell: ({ row }) => (
        <div className="flex gap-1 lowercase">
          <span>{row.original.daysOnMarket || '-'}</span>
          <span className="text-neutral-40">{t('table.days')}</span>
        </div>
      ),
      size: is2xl ? 140 : 140,
      enableSorting: true,
    }),
  ];

  return columns;
};

export const PriceChangesTable = ({
  data,
}: {
  data: ActivityReportUnitsTable[] | undefined;
}) => {
  const columns = useColumns();
  return (
    <div>
      <Table2 columns={columns} data={data || []} compact grayHeader={false} />
    </div>
  );
};
